/**
 * conference-week-list block
 *
 * Day-grouped event listing for conference landing pages. Events hang off a
 * dotted timeline rail under a day header, so a single day can carry several
 * events — the shape `rsa-event-row` cannot express, since it renders one
 * standalone row per block with its own calendar card.
 *
 * Layout: section header (eyebrow / heading / chips) then one group per day:
 * rail · day label · stacked event cards. Each card is copy on the left and a
 * square thumbnail on the right.
 *
 * Thumbnails are square by contract (`aspect-square` + `object-cover`). Supply
 * 1:1 artwork; anything else is centre-cropped.
 */
import { Image } from "../Image";

/** Lucide 0.446.0 glyphs, inlined — the repo has no icon-library dependency. */
const ICONS: Record<string, React.ReactNode> = {
  "arrow-right": <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
  "arrow-down": <><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></>,
  "store": <><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"/></>,
  "shopping-bag": <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></>,
  "calendar-days": <><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/></>,
  "map-pin": <><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></>,
  "bus": <><path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></>,
  "mic": <><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></>,
  "clock": <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>
};

function Icon({ name, className }: { name: string; className?: string }) {
  const glyph = ICONS[name];
  if (!glyph) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`shrink-0 ${className || ""}`}
    >
      {glyph}
    </svg>
  );
}

interface EventMeta {
  /** Lucide glyph name, e.g. "map-pin", "calendar-days", "bus", "clock". */
  icon?: string;
  text?: string;
}

interface WeekEvent {
  /** Small teal label above the title, e.g. "Expo hall hours" or "8:00 AM". */
  eyebrow?: string;
  /** Lucide glyph shown beside the eyebrow. */
  eyebrow_icon?: string;
  /** Pill beside the eyebrow, e.g. "Twelve seats". */
  chip?: string;
  heading?: string;
  /** One paragraph per entry. */
  copy?: string[];
  /** Emphasised closing line under the copy. */
  kicker?: string;
  /** Tinted callout box, e.g. the limited-run tote note. */
  note?: string;
  note_label?: string;
  note_icon?: string;
  meta?: EventMeta[];
  cta?: { text: string; url: string };
  /** Square (1:1) artwork. */
  image?: string;
  image_alt?: string;
  /** Speaker card shown in place of `image`: portrait (4:5, uncropped), then
   *  name with an optional company logo, then title — the same caption the
   *  Transform speaker grid uses. */
  speaker?: {
    name: string;
    title?: string;
    image: string;
    image_alt?: string;
    /** Square company logo tile, decorative. */
    logo?: string;
  };
}

interface WeekGroup {
  /** Day label, e.g. "Sept 22" or "All week". */
  label?: string;
  /** Secondary label, e.g. "Tuesday". */
  sublabel?: string;
  events?: WeekEvent[];
}

export function ConferenceWeekList({
  anchor_id,
  eyebrow,
  heading,
  chips,
  groups,
}: {
  /** Element id, so a hero CTA can jump here (e.g. "events"). */
  anchor_id?: string;
  eyebrow?: string;
  heading?: string;
  /** Summary pills on the right of the section header. */
  chips?: string[];
  groups?: WeekGroup[];
}) {
  return (
    <div id={anchor_id} className="bg-white scroll-mt-24">
      {(eyebrow || heading || chips?.length) && (
        <div className="px-4 md:px-8 pt-16 lg:pt-20">
          <div className="max-w-[1160px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-10 pb-7 border-b border-gray-100">
              <div>
                {eyebrow && (
                  <p className="font-body text-gray-60 text-[11px] font-semibold uppercase tracking-[0.08em] mb-3">
                    {eyebrow}
                  </p>
                )}
                {heading && (
                  <h2 className="font-display text-charcoal font-bold text-[28px] lg:text-40 leading-tight">
                    {heading}
                  </h2>
                )}
              </div>
              {chips && chips.length > 0 && (
                <ul className="flex flex-wrap gap-2 md:pb-1.5">
                  {chips.map((chip, i) => (
                    <li
                      key={chip}
                      className={`font-body text-[13px] font-medium rounded-md px-2.5 py-1.5 leading-tight whitespace-nowrap ${
                        i === 0
                          ? "text-teal-dark bg-teal-light/40 border border-teal-light"
                          : "text-gray-80 bg-gray-30 border border-gray-100"
                      }`}
                    >
                      {chip}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 md:px-8 pb-16 lg:pb-22">
        <div className="max-w-[1160px] mx-auto">
          {groups?.map((group, gi) => (
            <div key={group.label || gi} className="flex gap-4 lg:gap-5 pt-8 lg:pt-10">
              <div
                aria-hidden="true"
                className="hidden sm:flex w-3 shrink-0 flex-col items-center pt-1.5"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-teal-dark shrink-0" />
                <span className="flex-1 w-0 border-l border-dashed border-gray-60/60 mt-1.5" />
              </div>

              <div className="flex-1 min-w-0">
                {(group.label || group.sublabel) && (
                  <div className="flex items-baseline gap-3 mb-4">
                    {group.label && (
                      <span className="font-body text-charcoal text-[17px] font-semibold leading-tight">
                        {group.label}
                      </span>
                    )}
                    {group.sublabel && (
                      <span className="font-body text-gray-80 text-sm">
                        {group.sublabel}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-4">
                  {group.events?.map((event, ei) => (
                    <div
                      key={event.heading || ei}
                      className={`grid ${event.speaker ? "grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,1fr)_auto]"} gap-4 lg:gap-7 items-start bg-gray-10 border border-gray-100 rounded-lg p-4 lg:p-5.5 transition-colors duration-200 hover:border-gray-60/60`}
                    >
                      <div className="min-w-0">
                        {(event.eyebrow || event.chip) && (
                          <div className="flex items-center gap-2.5 flex-wrap mb-2">
                            {event.eyebrow && (
                              <span className="inline-flex items-center gap-1.5 font-body text-teal-dark text-[12.5px] font-semibold">
                                {event.eyebrow_icon && (
                                  <Icon name={event.eyebrow_icon} className="w-[15px] h-[15px]" />
                                )}
                                {event.eyebrow}
                              </span>
                            )}
                            {event.chip && (
                              <span className="font-body text-charcoal text-[10.5px] font-medium bg-gray-30 border border-gray-100 rounded-md px-1.5 py-0.5 leading-tight whitespace-nowrap">
                                {event.chip}
                              </span>
                            )}
                          </div>
                        )}

                        {event.heading && (
                          <h3 className="font-body text-charcoal font-bold text-[21px] leading-tight mb-2.5">
                            {event.heading}
                          </h3>
                        )}

                        {event.copy?.map((paragraph) => (
                          <p
                            key={paragraph.slice(0, 40)}
                            className="font-body text-gray-80 text-[14.5px] leading-relaxed max-w-[62ch] mb-2.5"
                          >
                            {paragraph}
                          </p>
                        ))}

                        {event.kicker && (
                          <p className="font-body text-charcoal text-[14.5px] font-medium leading-relaxed max-w-[62ch] mb-4">
                            {event.kicker}
                          </p>
                        )}

                        {event.note && (
                          <div className="flex gap-2.5 items-start bg-teal-light/30 border border-teal-light rounded-md px-3.5 py-3 mb-4 max-w-[62ch]">
                            {event.note_icon && (
                              <Icon
                                name={event.note_icon}
                                className="w-4 h-4 text-teal-dark mt-0.5"
                              />
                            )}
                            <p className="font-body text-teal-dark text-[13.5px] leading-snug">
                              {event.note_label && (
                                <span className="font-semibold">{event.note_label} </span>
                              )}
                              {event.note}
                            </p>
                          </div>
                        )}

                        {event.meta && event.meta.length > 0 && (
                          <ul className="flex flex-col gap-1.5 mb-4">
                            {event.meta.map((row) => (
                              <li key={row.text} className="flex items-center gap-2">
                                {row.icon && (
                                  <Icon
                                    name={row.icon}
                                    className="w-[15px] h-[15px] text-gray-60"
                                  />
                                )}
                                <span className="font-body text-gray-80 text-[13.5px]">
                                  {row.text}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {event.cta?.url && (
                          <a
                            href={event.cta.url}
                            target={event.cta.url.startsWith("http") ? "_blank" : undefined}
                            rel={event.cta.url.startsWith("http") ? "noopener" : undefined}
                            className="inline-flex items-center gap-1.5 bg-teal-cta hover:bg-teal-cta-hover text-white font-body text-[13px] font-semibold rounded-md px-3.5 py-2.5 leading-none transition-colors duration-200"
                          >
                            {event.cta.text}
                            <Icon name="arrow-right" className="w-[15px] h-[15px]" />
                          </a>
                        )}
                      </div>

                      {event.speaker ? (
                        <div className="flex items-center gap-4 md:block md:w-[168px] lg:w-[196px] shrink-0">
                          <div className="aspect-[4/5] w-[88px] md:w-full shrink-0 overflow-hidden rounded-lg border border-gray-100">
                            <Image
                              src={event.speaker.image}
                              alt={event.speaker.image_alt || event.speaker.name}
                              sizes="196px"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 md:mt-3">
                          <p className="font-display text-base lg:text-19 font-bold leading-tight text-charcoal">
                            {event.speaker.name.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="whitespace-nowrap">
                              {event.speaker.name.split(" ").slice(-1)}
                              {event.speaker.logo && (
                                <img
                                  src={event.speaker.logo}
                                  alt=""
                                  aria-hidden="true"
                                  width={28}
                                  height={28}
                                  className="ml-2 inline-block h-6 w-6 lg:h-7 lg:w-7 align-middle"
                                  loading="lazy"
                                />
                              )}
                            </span>
                          </p>
                          {event.speaker.title && (
                            <p className="mt-1 font-body text-sm lg:text-base text-gray-80">
                              {event.speaker.title}
                            </p>
                          )}
                          </div>
                        </div>
                      ) : event.image && (
                        /* aria-hidden + tabIndex -1: duplicates the card's own CTA, so it
                           is a pointer convenience only and must not become a second tab
                           stop. If it ever gets a distinct destination, remove both. */
                        <a
                          href={event.cta?.url || "#"}
                          target={event.cta?.url?.startsWith("http") ? "_blank" : undefined}
                          rel={event.cta?.url?.startsWith("http") ? "noopener" : undefined}
                          aria-hidden="true"
                          tabIndex={-1}
                          className="group block w-[88px] lg:w-[196px] aspect-square rounded-lg overflow-hidden border border-gray-100 shrink-0"
                        >
                          <Image
                            src={event.image}
                            alt={event.image_alt || ""}
                            sizes="196px"
                            className="w-full h-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-104"
                          />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
